import { useEffect, useState, useCallback } from "react"
import { getUsers, suspendUser, unsuspendUser, sendPasswordReset } from "@/services/user.service"
import type { UserWithSubscriptions } from "@/services/user.service"
import { useToast } from "@/context/ToastContext"
import { Badge, statusBadge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { Modal, FormGroup, Input, Select, Textarea } from "@/components/common/Modal"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { updateUserEmail, addAdminNote } from "@/services/user.service"
import { UserDetailModal } from "@/components/users/UserDetailModal"
import {
  Search, Download, Eye, Mail, KeyRound,
  StickyNote, Ban, CheckCircle, RefreshCw
} from "lucide-react"

const FILTERS = [
  { value: "all",         label: "All Users" },
  { value: "active",      label: "Active" },
  { value: "grace_period",label: "Grace Period" },
  { value: "suspended",   label: "Suspended" },
  { value: "inactive",    label: "Inactive" },
]

function productEmoji(slug: string): string {
  const map: Record<string, string> = {
    zimrah: "🌸", hpc: "📅", almanac: "🌾",
  }
  return map[slug] ?? "📱"
}

export function UsersPage() {
  const { toast } = useToast()
  const [users, setUsers]         = useState<UserWithSubscriptions[]>([])
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState("")
  const [filter, setFilter] = useState("all")
  const [selected, setSelected]   = useState<UserWithSubscriptions | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  // Email modal
  const [emailOpen, setEmailOpen]   = useState(false)
  const [emailUser, setEmailUser]   = useState<UserWithSubscriptions | null>(null)
  const [newEmail, setNewEmail]     = useState("")
  const [emailReason, setEmailReason] = useState("")
  const [emailSaving, setEmailSaving] = useState(false)

  // Suspend modal
  const [suspendOpen, setSuspendOpen]   = useState(false)
  const [suspendUser_, setSuspendUser_] = useState<UserWithSubscriptions | null>(null)
  const [suspendReason, setSuspendReason] = useState("")
  const [suspending, setSuspending]     = useState(false)

  // Note modal
  const [noteOpen, setNoteOpen]     = useState(false)
  const [noteUser, setNoteUser]     = useState<UserWithSubscriptions | null>(null)
  const [noteType, setNoteType]     = useState("general")
  const [noteTitle, setNoteTitle]   = useState("")
  const [noteContent, setNoteContent] = useState("")
  const [noteSaving, setNoteSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getUsers({ status: search })
      setUsers(data)
    } catch {
      toast("Failed to load users", "error")
    } finally {
      setLoading(false)
    }
  }, [search, toast])

  useEffect(() => { load() }, [load])

  // ── Actions ──────────────────────────────────────────────────

  const handlePasswordReset = async (u: UserWithSubscriptions) => {
    try {
      await sendPasswordReset(u.id, u.email)
      toast(`Password reset email sent to ${u.email}`, "success")
    } catch {
      toast("Failed to send reset email", "error")
    }
  }

  const handleUnsuspend = async (u: UserWithSubscriptions) => {
    try {
      await unsuspendUser(u.id)
      toast(`${u.full_name ?? u.email} unsuspended`, "success")
      load()
    } catch {
      toast("Failed to unsuspend user", "error")
    }
  }

  const confirmEmailChange = async () => {
    if (!emailUser || !newEmail.includes("@")) {
      toast("Valid email required", "error"); return
    }
    setEmailSaving(true)
    try {
      await updateUserEmail(emailUser.id, newEmail)
      toast("Email updated successfully", "success")
      setEmailOpen(false); setNewEmail(""); setEmailReason("")
      load()
    } catch {
      toast("Failed to update email", "error")
    } finally { setEmailSaving(false) }
  }

  const confirmSuspend = async () => {
    if (!suspendUser_ || !suspendReason) {
      toast("Reason required", "error"); return
    }
    setSuspending(true)
    try {
      await suspendUser(suspendUser_.id, suspendReason)
      toast(`${suspendUser_.full_name ?? suspendUser_.email} suspended`, "success")
      setSuspendOpen(false); setSuspendReason("")
      load()
    } catch {
      toast("Failed to suspend user", "error")
    } finally { setSuspending(false) }
  }

  const confirmNote = async () => {
    if (!noteUser || !noteContent) {
      toast("Note content required", "error"); return
    }
    setNoteSaving(true)
    try {
      await addAdminNote(noteUser.id, noteType, noteTitle, noteContent)
      toast("Note added", "success")
      setNoteOpen(false); setNoteTitle(""); setNoteContent("")
    } catch {
      toast("Failed to add note", "error")
    } finally { setNoteSaving(false) }
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zimrah-500 focus:border-transparent transition-all"
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all"
        >
          {FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => toast("Export coming in Phase 4", "info")}>Export</Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
            All Users
          </h2>
          <Badge variant="muted">{users.length} results</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["User", "Status", "Products", "Network", "Earnings", "Joined", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRow cols={7} />
              ) : users.length === 0 ? (
                <tr><td colSpan={7}>
                  <EmptyState icon="👥" title="No users found" subtitle="Try adjusting your search or filter" />
                </td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  {/* User */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-zimrah-500 to-zimrah-700 flex items-center justify-center text-white text-sm font-black shrink-0">
                        {(u.full_name ?? u.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-gray-900 truncate max-w-[160px]">
                          {u.full_name ?? "—"}
                        </p>
                        <p className="text-xs text-gray-400 truncate max-w-[160px]">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  {/* Status */}
                  <td className="px-6 py-4">
                    {statusBadge(u.subscription_status, u.is_suspended)}
                  </td>
                  {/* Products */}
                  <td className="px-6 py-4">
                    <div className="flex gap-1">
                      {u.subscriptions?.length > 0
                        ? u.subscriptions.map(s => (
                            <span key={s.id} title={s.product_slug} className="text-lg">
                              {productEmoji(s.product_slug)}
                            </span>
                          ))
                        : <span className="text-gray-300 text-sm">—</span>
                      }
                    </div>
                  </td>
                  {/* Network */}
                  <td className="px-6 py-4">
                    <span className="font-bold text-gray-700 tabular-nums">
                      {u.network_size ?? 0}
                    </span>
                  </td>
                  {/* Earnings */}
                  <td className="px-6 py-4">
                    <span className="font-bold text-gray-900 tabular-nums">
                      ${(u.total_earnings ?? 0).toFixed(2)}
                    </span>
                  </td>
                  {/* Joined */}
                  <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap">
                    {new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setSelected(u); setDetailOpen(true) }}
                        className="p-2 rounded-lg text-gray-400 hover:text-zimrah-600 hover:bg-zimrah-50 transition-all" title="View details">
                        <Eye size={15} />
                      </button>
                      <button onClick={() => { setEmailUser(u); setEmailOpen(true) }}
                        className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all" title="Change email">
                        <Mail size={15} />
                      </button>
                      <button onClick={() => handlePasswordReset(u)}
                        className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-all" title="Reset password">
                        <KeyRound size={15} />
                      </button>
                      <button onClick={() => { setNoteUser(u); setNoteOpen(true) }}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all" title="Add note">
                        <StickyNote size={15} />
                      </button>
                      {u.is_suspended
                        ? <button onClick={() => handleUnsuspend(u)}
                            className="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-all" title="Unsuspend">
                            <CheckCircle size={15} />
                          </button>
                        : <button onClick={() => { setSuspendUser_(u); setSuspendOpen(true) }}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all" title="Suspend">
                            <Ban size={15} />
                          </button>
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── User Detail Modal ── */}
      {selected && (
        <UserDetailModal
          user={selected}
          open={detailOpen}
          onClose={() => { setDetailOpen(false); setSelected(null) }}
          onRefresh={load}
        />
      )}

      {/* ── Change Email Modal ── */}
      <Modal open={emailOpen} onClose={() => setEmailOpen(false)} title="Change Email" icon="📧"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={emailSaving} onClick={confirmEmailChange}>Update Email</Button>
          </>
        }
      >
        <FormGroup label="Current Email">
          <Input value={emailUser?.email ?? ""} disabled />
        </FormGroup>
        <FormGroup label="New Email" required>
          <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="new@email.com" />
        </FormGroup>
        <FormGroup label="Reason">
          <Textarea value={emailReason} onChange={e => setEmailReason(e.target.value)} placeholder="Why is this email being changed?" />
        </FormGroup>
      </Modal>

      {/* ── Suspend Modal ── */}
      <Modal open={suspendOpen} onClose={() => setSuspendOpen(false)} title="Suspend User" icon="🚫"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button variant="danger" loading={suspending} onClick={confirmSuspend}>Suspend User</Button>
          </>
        }
      >
        <FormGroup label="User">
          <Input value={`${suspendUser_?.full_name ?? ""} (${suspendUser_?.email ?? ""})`} disabled />
        </FormGroup>
        <FormGroup label="Reason" required>
          <Select value={suspendReason} onChange={e => setSuspendReason(e.target.value)}>
            <option value="">Select a reason…</option>
            <option value="terms_violation">TOS Violation</option>
            <option value="fraud">Fraud</option>
            <option value="spam">Spam / Abuse</option>
            <option value="chargeback">Chargeback</option>
            <option value="other">Other</option>
          </Select>
        </FormGroup>
        <div className="mt-2 p-3 rounded-xl bg-red-50 border border-red-100">
          <p className="text-xs font-semibold text-red-600">
            ⚠️ This will immediately prevent the user from accessing their account.
          </p>
        </div>
      </Modal>

      {/* ── Add Note Modal ── */}
      <Modal open={noteOpen} onClose={() => setNoteOpen(false)} title="Add Note" icon="📝"
        footer={
          <>
            <Button variant="secondary" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={noteSaving} onClick={confirmNote}>Add Note</Button>
          </>
        }
      >
        <FormGroup label="User">
          <Input value={`${noteUser?.full_name ?? ""} (${noteUser?.email ?? ""})`} disabled />
        </FormGroup>
        <FormGroup label="Note Type">
          <Select value={noteType} onChange={e => setNoteType(e.target.value)}>
            <option value="general">General</option>
            <option value="support">Support</option>
            <option value="warning">Warning</option>
            <option value="issue">Issue</option>
            <option value="resolution">Resolution</option>
          </Select>
        </FormGroup>
        <FormGroup label="Title">
          <Input value={noteTitle} onChange={e => setNoteTitle(e.target.value)} placeholder="Brief summary…" />
        </FormGroup>
        <FormGroup label="Content" required>
          <Textarea value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Detailed note…" />
        </FormGroup>
      </Modal>
    </div>
  )
}